/**
 * Sales Mix Geographic Utility Functions
 * Computes location-based breakdowns for product consumption analysis
 */

/**
 * Get top city, store, and pincode by volume or value
 * @param {Array} orders - Filtered orders array
 * @param {string} metric - 'volume' or 'value'
 * @returns {Object} { topCity, topStore, topPincode }
 */
export function getTopCityStorePincodeByMetric(orders, metric = 'volume') {
    const cityMap = {};
    const storeMap = {};
    const pincodeMap = {};

    orders.forEach(order => {
        const cityId = order.cityId;
        const storeId = order.storeId;
        const pincode = order.pincode;

        // Calculate order metrics
        const orderVolume = order.items.reduce((sum, item) => sum + item.quantity, 0);
        const orderValue = order.total;

        // Aggregate by city
        if (!cityMap[cityId]) {
            cityMap[cityId] = {
                id: cityId,
                name: order.cityName || cityId,
                volume: 0,
                value: 0,
                orders: 0,
            };
        }
        cityMap[cityId].volume += orderVolume;
        cityMap[cityId].value += orderValue;
        cityMap[cityId].orders += 1;

        // Aggregate by store
        if (!storeMap[storeId]) {
            storeMap[storeId] = {
                id: storeId,
                name: order.storeName || storeId,
                cityId,
                volume: 0,
                value: 0,
                orders: 0,
            };
        }
        storeMap[storeId].volume += orderVolume;
        storeMap[storeId].value += orderValue;
        storeMap[storeId].orders += 1;

        // Aggregate by pincode
        if (!pincodeMap[pincode]) {
            pincodeMap[pincode] = {
                code: pincode,
                cityId,
                volume: 0,
                value: 0,
                orders: 0,
                codOrders: 0,
            };
        }
        pincodeMap[pincode].volume += orderVolume;
        pincodeMap[pincode].value += orderValue;
        pincodeMap[pincode].orders += 1;
        if (order.paymentMethod === 'COD') {
            pincodeMap[pincode].codOrders += 1;
        }
    });

    // Sort and get top
    const sortKey = metric === 'volume' ? 'volume' : 'value';

    const cities = Object.values(cityMap).sort((a, b) => b[sortKey] - a[sortKey]);
    const stores = Object.values(storeMap).sort((a, b) => b[sortKey] - a[sortKey]);
    const pincodes = Object.values(pincodeMap).sort((a, b) => b[sortKey] - a[sortKey]);

    // Add COD percentage to pincodes
    pincodes.forEach(p => {
        p.codPercent = p.orders > 0 ? (p.codOrders / p.orders) * 100 : 0;
    });

    return {
        topCity: cities[0] || null,
        topStore: stores[0] || null,
        topPincode: pincodes[0] || null,
        cities,
        stores,
        pincodes,
    };
}

/**
 * Get product breakdown by city (top 5)
 * @param {Array} orders - Filtered orders array
 * @param {string} productId - Product ID to analyze
 * @returns {Array} Top 5 cities with revenue, volume, orders, share
 */
export function getProductBreakdownByCity(orders, productId) {
    const cityMap = {};
    let totalRevenue = 0;
    let totalVolume = 0;
    let totalOrders = 0;

    orders.forEach(order => {
        order.items.forEach(item => {
            if (item.productId === productId) {
                const cityId = order.cityId;

                if (!cityMap[cityId]) {
                    cityMap[cityId] = {
                        id: cityId,
                        name: order.cityName || cityId,
                        revenue: 0,
                        volume: 0,
                        orders: 0,
                    };
                }

                cityMap[cityId].revenue += item.total;
                cityMap[cityId].volume += item.quantity;
                cityMap[cityId].orders += 1;

                totalRevenue += item.total;
                totalVolume += item.quantity;
                totalOrders += 1;
            }
        });
    });

    // Calculate share and sort
    const result = Object.values(cityMap)
        .map(city => ({
            ...city,
            revenueShare: totalRevenue > 0 ? (city.revenue / totalRevenue) * 100 : 0,
            volumeShare: totalVolume > 0 ? (city.volume / totalVolume) * 100 : 0,
        }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);

    return result;
}

/**
 * Get product breakdown by store (top 5)
 * @param {Array} orders - Filtered orders array
 * @param {string} productId - Product ID to analyze
 * @returns {Array} Top 5 stores with revenue, volume, orders, share
 */
export function getProductBreakdownByStore(orders, productId) {
    const storeMap = {};
    let totalRevenue = 0;
    let totalVolume = 0;

    orders.forEach(order => {
        order.items.forEach(item => {
            if (item.productId === productId) {
                const storeId = order.storeId;

                if (!storeMap[storeId]) {
                    storeMap[storeId] = {
                        id: storeId,
                        name: order.storeName || storeId,
                        revenue: 0,
                        volume: 0,
                        orders: 0,
                    };
                }

                storeMap[storeId].revenue += item.total;
                storeMap[storeId].volume += item.quantity;
                storeMap[storeId].orders += 1;

                totalRevenue += item.total;
                totalVolume += item.quantity;
            }
        });
    });

    const result = Object.values(storeMap)
        .map(store => ({
            ...store,
            revenueShare: totalRevenue > 0 ? (store.revenue / totalRevenue) * 100 : 0,
            volumeShare: totalVolume > 0 ? (store.volume / totalVolume) * 100 : 0,
        }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);

    return result;
}

/**
 * Get product breakdown by pincode (top 5)
 * @param {Array} orders - Filtered orders array
 * @param {string} productId - Product ID to analyze
 * @returns {Array} Top 5 pincodes with revenue, volume, orders, COD%, share
 */
export function getProductBreakdownByPincode(orders, productId) {
    const pincodeMap = {};
    let totalRevenue = 0;
    let totalVolume = 0;

    orders.forEach(order => {
        order.items.forEach(item => {
            if (item.productId === productId) {
                const pincode = order.pincode;

                if (!pincodeMap[pincode]) {
                    pincodeMap[pincode] = {
                        code: pincode,
                        revenue: 0,
                        volume: 0,
                        orders: 0,
                        codOrders: 0,
                    };
                }

                pincodeMap[pincode].revenue += item.total;
                pincodeMap[pincode].volume += item.quantity;
                pincodeMap[pincode].orders += 1;

                if (order.paymentMethod === 'COD') {
                    pincodeMap[pincode].codOrders += 1;
                }

                totalRevenue += item.total;
                totalVolume += item.quantity;
            }
        });
    });

    const result = Object.values(pincodeMap)
        .map(pin => ({
            ...pin,
            codPercent: pin.orders > 0 ? (pin.codOrders / pin.orders) * 100 : 0,
            revenueShare: totalRevenue > 0 ? (pin.revenue / totalRevenue) * 100 : 0,
            volumeShare: totalVolume > 0 ? (pin.volume / totalVolume) * 100 : 0,
        }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);

    return result;
}

/**
 * Get top city for a specific product
 * @param {Array} orders - Filtered orders array
 * @param {string} productId - Product ID to analyze
 * @returns {Object|null} Top city with revenue and volume
 */
export function getTopCityForProduct(orders, productId) {
    const breakdown = getProductBreakdownByCity(orders, productId);
    return breakdown[0] || null;
}

/**
 * Get product distribution data with top city info for each product
 * @param {Array} orders - Filtered orders array
 * @param {Array} productData - Aggregated product data with revenue/volume
 * @param {number} topN - Number of products to include
 * @returns {Array} Products with their top city information
 */
export function getProductDistributionByCity(orders, productData, topN = 10) {
    // Get top products by value
    const topProducts = [...productData]
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, topN);

    return topProducts.map(product => {
        const topCity = getTopCityForProduct(orders, product.id);

        return {
            ...product,
            topCityName: topCity?.name || 'N/A',
            topCityRevenue: topCity?.revenue || 0,
            topCityVolume: topCity?.volume || 0,
            topCityShare: topCity?.revenueShare || 0,
        };
    });
}

export default {
    getTopCityStorePincodeByMetric,
    getProductBreakdownByCity,
    getProductBreakdownByStore,
    getProductBreakdownByPincode,
    getTopCityForProduct,
    getProductDistributionByCity,
};
